import React, { useEffect, useRef } from 'react';
import Moment from 'react-moment';
import {
  useGetApplicationQueue,
  useEditApplication,
  useStore,
  useIsUser,
  useIsAdmin,
  useIsManager,
  queryForApi,
} from 'hooks';
import { ApplicationSupplyService } from 'services';
import Table from 'components/table';
import Modal from 'components/modal';
import Application from 'components/modal/order/supply';
import ActionsTable from 'components/table/actions';
import { Input } from 'components/ui/form';
import { Actions, Filter } from 'components/applications';
import { WAGON_UTENSILS } from 'helpers/constants';
import styles from './queue.module.scss';

const scrollMainToTop = () => {
  const main = document.querySelector('main');
  if (main) main.scrollTo({ top: 0, behavior: 'smooth' });
};

const formatWeight = (v) => (v != null && !Number.isNaN(Number(v))
  ? new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(v))
  : '—');

const QueuePage = ({
  history,
  location: { pathname, search },
  match: {
    params: { userId },
  },
}) => {
  const { user, setReload, setUser, setLoading } = useStore();
  const isUser = useIsUser();
  const isAdmin = useIsAdmin();
  const isManager = useIsManager();
  const showFilter = true;
  const [
    data,
    filters,
    query,
    setData,
    setQueryAndResetPage,
    totalCount,
    page,
    limit,
    setPage,
    setLimit,
    resetFilters,
  ] = useGetApplicationQueue(userId, showFilter);
  const [edit] = useEditApplication(data, search);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    scrollMainToTop();
  }, [query]);

  const onCloseModal = (isReload = false) => {
    history.push({ pathname });

    if (isReload) {
      setReload();
    }
  };

  const onExportExcel = async () => {
    setLoading(true);
    const params = { userId, ...queryForApi(query) };
    const blob = await new ApplicationSupplyService({ setUser }).exportQueueExcel(params);
    setLoading(false);
    if (blob) {
      const href = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = `queue_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(href);
    }
  };

  const cells = [{
    name: 'Номер в очереди',
    nowrap: true,
    onRender: (item, value, index) => page * limit + index + 1,
  }, {
    name: 'Номер',
    renderField: 'id',
    nowrap: true,
  }, {
    name: 'Дата',
    renderField: 'date',
    nowrap: true,
    onRender: (item, value) => <Moment>{value}</Moment>,
  }, {
    name: 'Продукт',
    onRender: item => item?.contractProduct?.product?.name || '',
  }, {
    name: 'Станция',
    wrapText: true,
    onRender: item => item?.applicationWagon?.station?.name || '',
  }, ...(isAdmin || isManager ? [{
    name: 'Направление',
    wrapText: true,
    onRender: item => item?.applicationWagon?.station?.direction?.name || '',
  }] : []), {
    name: 'Номер ГУ-12',
    onRender: item => item?.applicationWagon?.gy12 || '',
  }, ...(!isUser ? [{
    name: 'Вид вагона',
    onRender: item => item?.applicationWagon?.wagon?.name || '—',
  }, {
    name: 'Принадлежность',
    onRender: item => WAGON_UTENSILS.toString(item?.applicationWagon?.utensils) || '—',
  }] : []), {
    name: 'Кол-во тонн',
    nowrap: true,
    onRender: item => formatWeight(Number(item.ton) - Number(item.shippedTon)),
  }, {
    keyName: 'actions',
    right: true,
    nowrap: true,
    onRender: ({
      id,
      userId: applicationUserId,
    }) => (isUser && applicationUserId === user.id) || isManager || isAdmin ? (
      <ActionsTable
        data={[{
          name: 'edit',
          title: 'Редактировать',
          onClick: () => {
            history.push({
              pathname,
              search: `?id=${id}`,
            });
          },
        }]}
      />
    ) : null,
  }];

  return (
    <>
      {(isAdmin || isManager) && (
        <Actions
          exportText="Выгрузить в Excel"
          onExport={onExportExcel}
        />
      )}
      <div className={styles.search}>
        <Input
          type="text"
          value={query.search || ''}
          onChange={e => setQueryAndResetPage({ search: e.target.value })}
          placeholder="Поиск по заявке, направлению, грузополучателю, станции, договору"
        />
      </div>
      <Filter
        hideMenu
        filters={filters}
        query={query}
        setQuery={setQueryAndResetPage}
        showFilter={showFilter}
        onResetFilters={resetFilters}
      />
      <Table
        data={data}
        cells={cells}
        count={totalCount}
        page={page}
        limit={limit}
        onPageChange={({ selected }) => setPage(selected)}
        onLimitChange={(newLimit) => {
          setLimit(newLimit);
          setPage(0);
        }}
      />
      {edit ? (
        <Modal closeBackground={false} closeEsc onClose={onCloseModal}>
          <Application edit={edit} onClose={onCloseModal} />
        </Modal>
      ) : null}
    </>
  );
};

export default QueuePage;
